import React, { useCallback, useEffect, useRef, useState } from 'react';

import FormHeader from 'components/FormHeader/index';
import { Theme } from 'enums/Theme';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { LoadingType } from 'prequalificationV2/actions/index';
import Loader from 'components/Loader/index';
import { ProgressBarStatus } from 'enums/ProgressBarStatus';
import { ACCEPTED_OFFER_HEADER_TITLE } from 'prequalificationV2/pages/Dashboard/steps/AcceptOffer/acceptOffer.constants';
import { OfferResponseDto } from 'api/AutomatedOffersApi';
import { ApplicationProductName } from 'enums/common.enums';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import OffersDetails from 'prequalificationV2/pages/Dashboard/AcceptedOfferDetails/OffersDetails/OffersDetails';

import UpdateLoanTerms from './UpdateLoanTerms/UpdateLoanTerms';
import { getOfferApi } from './acceptOfferDetails.utils';
import { LENDERS_ONLY_ALLOWED_TO_VIEW_OFFER } from './acceptOfferDetails.constants';

const AcceptedOfferDetails = () => {
  const { applicationId, productName, variables } = useAppSelector((state) => state.application);
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.DashboardPageUpdating]);
  const [isLoading, setIsLoading] = useState(false);
  const [offer, setOffer] = useState<OfferResponseDto>();

  const lender = variables?.lender;

  const offerApi = useRef(() =>
    getOfferApi({
      productName: productName as ApplicationProductName,
      hasAutomatedOffer: variables!.hasAutomatedOffer as boolean,
    }),
  );

  const getOffer = useCallback(
    async (loanAmount: number, loanTerm: string) => {
      const getLoans = async () => {
        try {
          setIsLoading(true);

          const api = offerApi.current();
          const response = await api.getOffer(applicationId!, loanAmount, loanTerm);

          setOffer(response);
        } catch (error) {
          notificationManager.notifyFailure(FailureNotificationType.UnknownError);
        } finally {
          setIsLoading(false);
        }
      };

      getLoans();
    },
    [applicationId],
  );

  useEffect(() => {
    if (LENDERS_ONLY_ALLOWED_TO_VIEW_OFFER.includes(lender!)) {
      return;
    }

    getOffer(variables!.loan_amount!, variables!.loan_term!);
  }, [applicationId, getOffer, lender, variables]);

  const checkIsOfferAllowedToUpdate = () => {
    if (LENDERS_ONLY_ALLOWED_TO_VIEW_OFFER.includes(lender!)) {
      return false;
    }

    if (variables!.lender_2) {
      return false;
    }

    if (offer && offer.maxLoanAmount < offer.minLoanAmount) {
      return false;
    }

    return true;
  };

  return isGlobalLoading || isLoading ? (
    <Loader />
  ) : (
    <>
      <FormHeader
        isBorderRounded={false}
        title={ACCEPTED_OFFER_HEADER_TITLE}
        theme={Theme.Dark}
        percent={ProgressBarStatus.Done}
      />
      {checkIsOfferAllowedToUpdate() ? <UpdateLoanTerms /> : <OffersDetails />}
    </>
  );
};

export default AcceptedOfferDetails;
